import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DefaultImage from '../DefaultImage';
import { clickCarouselThumbnail } from '../../store/actions/dataLayer';
import {
  MAX_CAROUSEL_THUMBNAILS_WIDTH,
  CAROUSEL_THUMBNAILS_NUMBER
} from '../../constants/boatDetails';
import './styles.css';

const ThumbnailImage = ({ itemThumbnail }) => {
  const [defaultImage, setDefaultImage] = useState(false);

  const handleThumbError = () => {
    setDefaultImage(true);
  };

  return (
    <>
      {defaultImage ? (
        <DefaultImage size="40" withText={false} />
      ) : (
        <img
          draggable="false"
          className="carousel-thumbs__img"
          onError={handleThumbError}
          src={itemThumbnail}
          alt="carousel thumbnail"
        />
      )}
    </>
  );
};

ThumbnailImage.propTypes = {
  /** function that will be called on click a thubnail */
  itemThumbnail: PropTypes.string
};

const CarouselThumbs = ({
  device,
  mediaItems,
  selectedItem,
  clickCarouselThumbnail,
  handleThumbClick,
  carouselButtonClick,
  onDrag
}) => {
  const [state, setState] = useState({
    firstSelectedItem: selectedItem,
    index: 0,
    maxIndexNum: parseInt(mediaItems.length / CAROUSEL_THUMBNAILS_NUMBER),
    controlledPosition: {
      x: 0,
      y: 0
    },
    maxPixelsToMove: 0,
    totalPixelsLength: 0,
    maxIndexPixels: 0,
    touchScrolled: false
  });

  const {
    firstSelectedItem,
    index,
    controlledPosition,
    maxPixelsToMove,
    totalPixelsLength,
    maxIndexPixels,
    touchScrolled
  } = state;

  const handleThumbnailClick = (event, key) => {
    setState((prevState) => ({
      ...prevState,
      firstSelectedItem: key
    }));
    clickCarouselThumbnail();
    handleThumbClick(event, key);
  };

  const handleTouchStart = () => {
    setState((prevState) => ({
      ...prevState,
      touchScrolled: false
    }));
  };

  const handleTouchMove = () => {
    setState((prevState) => ({
      ...prevState,
      touchScrolled: true
    }));
  };

  const handleTouchEnd = (event, key) => {
    if (!touchScrolled) {
      handleThumbnailClick(event, key);
    }
  };

  const handlePrevArrow = () => {
    const { x, y } = controlledPosition;
    setState((prevState) => ({
      ...prevState,
      index: Math.max(prevState.index - 1, 0),
      controlledPosition: {
        x:
          prevState.index === state.maxIndexNum
            ? x + (totalPixelsLength - prevState.index * maxPixelsToMove)
            : Math.min(x + maxPixelsToMove, 0),
        y
      }
    }));

    carouselButtonClick();
  };

  const handleNextArrow = () => {
    const { x, y } = controlledPosition;
    setState((prevState) => ({
      ...prevState,
      index: Math.min(prevState.index + 1, state.maxIndexNum),
      controlledPosition: {
        x: Math.max(x - maxPixelsToMove, maxIndexPixels),
        y
      }
    }));

    carouselButtonClick();
  };

  const onControlledDrag = (e, position) => {
    const { x } = position;
    setState((prevState) => ({
      ...prevState,
      controlledPosition: {
        x,
        y: 0
      }
    }));

    if (onDrag) {
      onDrag();
    }
  };

  const showSelectedItemThumbnail = (selectedItem) => {
    const thumbStep = parseInt(selectedItem / CAROUSEL_THUMBNAILS_NUMBER);
    const positionsToMove = index - thumbStep;
    const { x, y } = controlledPosition;

    if (positionsToMove < 0) {
      setState((prevState) => ({
        ...prevState,
        index: Math.min(prevState.index - positionsToMove, state.maxIndexNum),
        controlledPosition: {
          x: Math.max(
            (x - maxPixelsToMove) * positionsToMove * -1,
            maxIndexPixels
          ),
          y
        },
        firstSelectedItem: selectedItem
      }));
    } else if (positionsToMove > 0) {
      setState((prevState) => ({
        ...prevState,
        index: Math.max(prevState.index - positionsToMove, 0),
        controlledPosition: {
          x:
            prevState.index === state.maxIndexNum
              ? x +
                (totalPixelsLength -
                  prevState.index * maxPixelsToMove +
                  maxPixelsToMove * (positionsToMove - 1))
              : Math.min(x + maxPixelsToMove * positionsToMove, 0),
          y
        },
        firstSelectedItem: selectedItem
      }));
    }
  };

  useEffect(() => {
    let maxPixelsToMove = Math.min(
      window.innerWidth,
      MAX_CAROUSEL_THUMBNAILS_WIDTH
    );
    setState((prevState) => ({
      ...prevState,
      maxPixelsToMove: maxPixelsToMove,
      totalPixelsLength:
        (maxPixelsToMove / CAROUSEL_THUMBNAILS_NUMBER) * mediaItems.length,
      maxIndexPixels:
        -1 *
        ((mediaItems.length / CAROUSEL_THUMBNAILS_NUMBER) * maxPixelsToMove -
          maxPixelsToMove)
    }));

    if (firstSelectedItem > CAROUSEL_THUMBNAILS_NUMBER - 1) {
      showSelectedItemThumbnail(firstSelectedItem);
    }
  }, [firstSelectedItem, mediaItems.length]);

  useEffect(() => {
    if (selectedItem !== firstSelectedItem) {
      showSelectedItemThumbnail(selectedItem);
    }
  }, [selectedItem, firstSelectedItem]);

  return (
    <div className="thumbs-wrapper" id="thumbs-wrapper">
      <div className="carousel-thumbs" id="carousel-thumbs">
        <Draggable
          onStart={() => device !== 'desktop'}
          axis="x"
          bounds={{ left: maxIndexPixels, right: 0 }}
          position={controlledPosition}
          onDrag={onControlledDrag}
          id="carousel-thumbs-draggable"
        >
          <div className="carousel-thumbs__wrapper">
            {mediaItems.length !== 0 &&
              mediaItems.map((item, key) => {
                let itemThumbnail;
                if (
                  item.mediaType === 'image' ||
                  item.mediaType === '360Image'
                ) {
                  itemThumbnail = item.url;
                } else {
                  itemThumbnail = item.videoVideoThumbnailUri;
                }
                return (
                  <div
                    className={classnames('carousel-thumbs__img-wrapper', {
                      selected: selectedItem === key
                    })}
                    key={key}
                    id={`carousel-thumbs-${key}`}
                    onClick={(event) => handleThumbnailClick(event, key)}
                    onTouchStart={() => handleTouchStart()}
                    onTouchMove={() => handleTouchMove()}
                    onTouchEnd={(event) => handleTouchEnd(event, key)}
                  >
                    <ThumbnailImage itemThumbnail={itemThumbnail} />
                  </div>
                );
              })}
          </div>
        </Draggable>
      </div>
      {mediaItems.length > CAROUSEL_THUMBNAILS_NUMBER && (
        <div
          onClick={handlePrevArrow}
          className="control-arrow control-prev"
          aria-label="control-prev"
        ></div>
      )}
      {mediaItems.length > CAROUSEL_THUMBNAILS_NUMBER && (
        <div
          onClick={handleNextArrow}
          className="control-arrow control-next"
          aria-label="control-next"
        ></div>
      )}
    </div>
  );
};

CarouselThumbs.defaultProps = {
  selectedItem: 0
};

CarouselThumbs.propTypes = {
  device: PropTypes.string,
  /** Array of the items that will be shown as thumbnails */
  mediaItems: PropTypes.array,
  selectedItem: PropTypes.number,
  clickCarouselThumbnail: PropTypes.func,
  /** function that will be called on click a thumbnail */
  handleThumbClick: PropTypes.func,
  /** function that will be called on click a next/prev arrow */
  carouselButtonClick: PropTypes.func,
  /** function that will be called when the thumbnails container detects a drag event */
  onDrag: PropTypes.func
};

export default connect(null, (dispatch) =>
  bindActionCreators(
    {
      clickCarouselThumbnail
    },
    dispatch
  )
)(CarouselThumbs);
